import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {addItemsToCart, loadColourPrice, loadProducts} from './api';
import ColourChooser, {colourPayload} from './ColourChooser';
import Modal from './Modal';
import Spinner from './Spinner';
import {uniqueSkus} from './tree';

/**
 * Eine Linkempfehlung nimmt denselben sortierten Empfehlungsslot wie ein Produkt
 * ein, besitzt aber keine Katalog-, Preis- oder Warenkorblogik. Linktext und URL
 * stammen bereits scopespezifisch aus Magento.
 */
export function localizedRecommendationValue(recommendation, field, language) {
  // Der Magento-Endpunkt liefert normalerweise bereits den für den Store Scope
  // aufgelösten Feldwert. Zusätzlich unterstützen wir explizite Sprachfelder,
  // damit importierte oder künftig erweiterte Datenpakete ohne Umbau darstellbar
  // bleiben. Deutsch ist ausschließlich der letzte Kompatibilitäts-Fallback.
  const localized = recommendation?.[`${field}_${language}`];
  if (typeof localized === 'string' && localized.trim()) return localized;
  const resolved = recommendation?.[field];
  if (typeof resolved === 'string' && resolved.trim()) return resolved;
  const german = recommendation?.[`${field}_de`];
  return typeof german === 'string' ? german : '';
}

function LinkRecommendation({recommendation, language}) {
  if (!recommendation.link_text || !recommendation.link_url) return null;
  const information = localizedRecommendationValue(recommendation, 'link_info', language);

  return (
    <article className="pf-recommendation-link">
      {information && <p className="pf-product__remarks">{information}</p>}
      <a className="pf-button pf-button--primary" href={recommendation.link_url}>
        {recommendation.link_text}
      </a>
    </article>
  );
}

/**
 * Eine Empfehlungskarte hält nur die vom Kunden veränderbaren Werte: Variante,
 * Menge, Farbe, Expresswunsch und Teilnahme an „alle hinzufügen“.
 */
function ProductCard({recommendation, product, context, existingKeys, language, t, onState, onAdd, adding}) {
  const variants = useMemo(() => {
    // Die ursprünglich empfohlene SKU wird mit ihren Kotori_Variants-Geschwistern
    // zusammengeführt. Die SKU bleibt der eindeutige Schlüssel im Frontend.
    const found = new Map();
    [product, ...(product?.variants || [])].forEach((item) => { if (item?.sku) found.set(item.sku, item); });
    return [...found.values()];
  }, [product]);
  const [sku, setSku] = useState(product?.sku || recommendation.sku || '');
  const [qty, setQty] = useState(1);
  const [included, setIncluded] = useState(Boolean(product?.available));
  const [colour, setColour] = useState(null);
  const [express, setExpress] = useState(false);
  const [price, setPrice] = useState(null);
  const selectedProduct = variants.find((item) => item.sku === sku) || product;
  const remarks = localizedRecommendationValue(recommendation, 'remarks', language);

  useEffect(() => {
    // Neu geladene Katalogdaten initialisieren die Karte mit der empfohlenen SKU.
    setSku(product?.sku || recommendation.sku || '');
    setIncluded(Boolean(product?.available));
  }, [product, recommendation.sku]);

  useEffect(() => {
    // Farbe und Preis gehören zur konkreten Variante und werden beim Wechsel
    // niemals ungeprüft auf eine andere SKU übertragen.
    setColour(null); setExpress(false); setPrice(null);
  }, [sku]);

  useEffect(() => {
    // Preisabfragen erfolgen erst nach valider Farbauswahl. Sie bleiben bewusst
    // live, weil Warenkorbinhalt, Menge und Expresswunsch den Endpreis verändern.
    if (!selectedProduct?.colour_configurable || !colour) { setPrice(null); return undefined; }
    let active = true;
    loadColourPrice(selectedProduct.sku, qty, colour, express)
      .then((data) => { if (active) setPrice(data.success ? data : null); })
      .catch(() => { if (active) setPrice(null); });
    return () => { active = false; };
  }, [selectedProduct?.sku, selectedProduct?.colour_configurable, qty, colour, express]);

  const state = useMemo(() => ({
    // Der normalisierte Zustand ist zugleich Validierungsbasis und Payload für
    // Einzel- sowie Batch-Hinzufügungen. Beide Wege benutzen damit dieselben Regeln.
    included,
    valid: Boolean(selectedProduct?.available) && (!selectedProduct?.colour_configurable || Boolean(colour)),
    specialKey: colour?.is_special ? colour.key : '',
    item: {
      sku: selectedProduct?.sku || sku,
      qty,
      ...colourPayload(colour, express)
    }
  }), [included, selectedProduct, sku, qty, colour, express]);

  useEffect(() => { onState(recommendation.position, state); }, [onState, recommendation.position, state]);

  if (!product?.available) {
    return (
      <article className="pf-product pf-product--unavailable">
        <div><h3>{recommendation.sku || recommendation.old_sku}</h3><p>{t('productUnavailable')}</p></div>
        {remarks && <p className="pf-product__remarks">{remarks}</p>}
      </article>
    );
  }

  const displayPrice = price?.formatted_unit_price || selectedProduct.formatted_price;
  const prefix = selectedProduct.colour_configurable && !colour ? `${t('from')} ` : '';
  return (
    <article className="pf-product">
      <label className="pf-product__include">
        <input type="checkbox" checked={included} onChange={(event) => setIncluded(event.target.checked)} />
        <span>{t('include')}</span>
      </label>
      <div className="pf-product__image">
        <a href={selectedProduct.url}><img src={selectedProduct.image} alt={selectedProduct.name} loading="lazy" /></a>
      </div>
      <div className="pf-product__body">
        <h3><a href={selectedProduct.url}>{selectedProduct.name}</a></h3>
        <p className="pf-product__sku">{t('sku')}: {selectedProduct.sku}</p>
        {remarks && <p className="pf-product__remarks">{remarks}</p>}

        {variants.length > 1 && (
          <label className="pf-field">
            <span>{t('variant')}</span>
            <select value={sku} onChange={(event) => setSku(event.target.value)}>
              {variants.map((variant) => <option value={variant.sku} key={variant.sku}>{variant.variant_label}</option>)}
            </select>
          </label>
        )}

        {selectedProduct.colour_configurable && (
          <ColourChooser sku={selectedProduct.sku} allowedColourTypes={selectedProduct.allowed_colour_types} value={colour} onChange={(next) => { setColour(next); if (!next?.is_special) setExpress(false); }} existingKeys={existingKeys} t={t} />
        )}

        {colour?.is_special && (
          <label className="pf-express">
            <input type="checkbox" checked={express} onChange={(event) => setExpress(event.target.checked)} />
            <span><strong>{t('express')}</strong><small>{t('expressHint')}</small></span>
          </label>
        )}
      </div>
      <div className="pf-product__buy">
        <strong className="pf-product__price">{prefix}{displayPrice}</strong>
        <small>{context.tax_shipping_notice || t('tax')}</small>
        {price && colour?.is_special && (
          <small className="pf-product__fee">{Number(price.surcharge_amount || 0) > 0 ? t('feeIncluded') : t('noAdditionalFee')}</small>
        )}
        <label className="pf-qty"><span>{t('quantity')}</span><input type="number" min="1" max="99" step="1" value={qty} onChange={(event) => setQty(Math.max(1, Number.parseInt(event.target.value || '1', 10)))} /></label>
        <button type="button" className="pf-button pf-button--primary" disabled={adding || !included} onClick={() => onAdd(state)}>{adding ? t('adding') : t('add')}</button>
      </div>
    </article>
  );
}

export default function Recommendations({recommendations, language = 'de', t, selection, onAnalytics}) {
  const [products, setProducts] = useState(new Map());
  const [context, setContext] = useState({});
  const [states, setStates] = useState({});
  const [existingKeys, setExistingKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState('');
  const productRecommendations = useMemo(
    () => recommendations.filter((recommendation) => recommendation.type !== 'link'),
    [recommendations]
  );

  useEffect(() => {
    // Der Entscheidungsbaum bleibt klein und cachebar. Veränderliche Katalogdaten
    // wie Preis, Bild, Verfügbarkeit und Varianten werden erst im Ergebnis geladen.
    const skus = uniqueSkus(productRecommendations);
    setLoading(true); setStates({});
    if (!skus.length) { setProducts(new Map()); setLoading(false); return undefined; }
    let active = true;
    loadProducts(skus)
      .then((data) => {
        if (!active) return;
        setProducts(new Map((data.items || []).map((item) => [item.sku, item])));
        setContext(data.context || {});
        setExistingKeys(data.context?.existing_special_colour_keys || []);
      })
      .catch(() => { if (active) setMessage(t('loadError')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [productRecommendations, t]);

  const updateState = useCallback((position, state) => {
    // Die Empfehlungsposition ist stabil und entspricht der Reihenfolge im Backend.
    setStates((current) => ({...current, [position]: state}));
  }, []);

  const add = useCallback(async (selectedStates, eventType) => {
    // Vor dem Request werden alle ausgewählten Karten gemeinsam validiert. Es
    // gibt keine alerts und keine teilweise unbemerkte Übertragung ohne Farbe.
    const chosen = selectedStates.filter((state) => state?.included);
    if (!chosen.length) { setMessage(t('colourRequired')); return; }
    if (chosen.some((state) => !state.valid)) { setMessage(t('colourRequired')); return; }
    setAdding(true);
    try {
      await addItemsToCart(chosen.map((state) => state.item), context);
      // Erst der serverseitig bestätigte Warenkorberfolg zählt als Conversion.
      // Bei Einzelartikeln kann zusätzlich die SKU anonym aggregiert werden.
      onAnalytics(eventType, selection, eventType === 'add_single' ? chosen[0].item.sku : '');
      // Nach erfolgreichem Hinzufügen kostet dieselbe konkrete Sonderfarbe bei
      // weiteren Produkten auf dieser Seite CHF 0. Die Anzeige wird sofort aktualisiert.
      const keys = chosen.map((state) => state.specialKey).filter(Boolean);
      if (keys.length) setExistingKeys((current) => [...new Set([...current, ...keys])]);
    } catch (error) {
      setMessage(error.message || t('addError'));
    } finally {
      setAdding(false);
    }
  }, [context, onAnalytics, selection, t]);

  if (loading) return <Spinner label={t('loading')} />;
  if (!recommendations.length) return <p className="pf-status">{t('noResults')}</p>;

  return (
    <>
      <div className="pf-products">
        {recommendations.map((recommendation) => (
          recommendation.type === 'link'
            ? <LinkRecommendation recommendation={recommendation} language={language} key={recommendation.position} />
            : <ProductCard
                recommendation={recommendation}
                product={products.get(recommendation.sku)}
                context={context}
                existingKeys={existingKeys}
                language={language}
                t={t}
                onState={updateState}
                onAdd={(state) => add([state], 'add_single')}
                adding={adding}
                key={recommendation.position}
              />
        ))}
      </div>
      {productRecommendations.length > 0 && (
        <div className="pf-results-actions">
          <button type="button" className="pf-button pf-button--primary" disabled={adding} onClick={() => add(Object.values(states), 'add_all')}>{adding ? t('adding') : t('addAll')}</button>
        </div>
      )}
      <Modal message={message} closeLabel={t('close')} onClose={() => setMessage('')} />
    </>
  );
}
